import { useRef, useMemo } from 'react'
import { formatCurrency } from '@/utils/format-currency'
import type { LayoutConfig } from '@/utils/dealCalculations'
import type { PlanServiceItem } from '@/utils/dealCalculations'
import { normalizeTextEncoding } from '@/utils/text-normalization'
import type { ComboVariationWithPricing } from '@/types'
import {
  computeCategoryBlockNumbers,
  computeSlideCategoryBlocks,
} from './computeSlideCategoryBlocks'
import { useSlidePreviewDrag } from './useSlidePreviewDrag'
import { SlidePreviewCover } from './SlidePreviewCover'
import { SlidePreviewInvestmentView } from './SlidePreviewInvestmentView'

interface SlidePreviewProps {
  layout: LayoutConfig
  onLayoutChange: (newLayout: LayoutConfig) => void
  planName: string
  planDescription: string
  supportHoursLabel?: string
  supportTierLines?: string[]
  planTipo: string
  planValue: number
  isRecurring: boolean
  paymentDetails: string
  loyaltyDuration: string
  negotiationDeadlineLabel?: string
  bonusText?: string
  serviceGroups: Map<string, PlanServiceItem[]>
  projectType?: string
  hidePricing?: boolean
  priceCompareFrom?: number
  isCover?: boolean
  variations?: ComboVariationWithPricing[]
  empresaNome?: string
}

export function SlidePreview({
  layout,
  onLayoutChange,
  planName,
  planDescription,
  supportHoursLabel = '',
  supportTierLines = [],
  planTipo,
  planValue,
  isRecurring,
  paymentDetails,
  loyaltyDuration,
  negotiationDeadlineLabel = '',
  bonusText = '',
  serviceGroups,
  projectType,
  hidePricing = false,
  priceCompareFrom,
  isCover = false,
  variations,
  empresaNome,
}: SlidePreviewProps) {
  const previewRef = useRef<HTMLDivElement>(null)
  const leftBlockWidth = layout.leftBlockWidth || 35

  const categoryBlockNumbers = useMemo(
    () => computeCategoryBlockNumbers(layout, serviceGroups),
    [layout, serviceGroups],
  )

  const categoryBlocks = useMemo(
    () => computeSlideCategoryBlocks(layout, serviceGroups, leftBlockWidth),
    [serviceGroups, layout, leftBlockWidth],
  )

  const drag = useSlidePreviewDrag(layout, onLayoutChange, categoryBlocks, leftBlockWidth)

  if (isCover && variations && variations.length > 0) {
    return <SlidePreviewCover previewRef={previewRef} empresaNome={empresaNome} variations={variations} />
  }

  const multiSupportTiers = supportTierLines.length >= 2
  const priceLabel = isRecurring ? `${formatCurrency(planValue)}/mês` : formatCurrency(planValue)
  const showDePor =
    !hidePricing &&
    !multiSupportTiers &&
    priceCompareFrom !== undefined &&
    priceCompareFrom > planValue + 0.009
  const priceCompareLabel = showDePor
    ? `${formatCurrency(priceCompareFrom)}/mês`
    : undefined

  return (
    <SlidePreviewInvestmentView
      previewRef={previewRef}
      layout={layout}
      leftBlockWidth={leftBlockWidth}
      priceLabel={priceLabel}
      priceCompareLabel={priceCompareLabel}
      displayPlanName={normalizeTextEncoding(planName)}
      displayPlanDescription={normalizeTextEncoding(planDescription)}
      displaySupportHoursLabel={normalizeTextEncoding(supportHoursLabel)}
      displaySupportTierLines={supportTierLines.map((line) => normalizeTextEncoding(line))}
      hidePricing={hidePricing}
      displayBonus={normalizeTextEncoding(bonusText)}
      displayProjectType={normalizeTextEncoding(projectType || '')}
      displayPayment={normalizeTextEncoding(paymentDetails)}
      displayLoyalty={normalizeTextEncoding(loyaltyDuration)}
      displayNegotiationDeadline={normalizeTextEncoding(negotiationDeadlineLabel)}
      planTipo={planTipo}
      projectType={projectType}
      draggingKey={drag.draggingKey}
      snapGuides={drag.snapGuides}
      categoryBlocks={categoryBlocks}
      categoryBlockNumbers={categoryBlockNumbers}
      onDragStart={drag.handleDragStart}
      onMouseMove={drag.handleMouseMove}
      onMouseUpOrLeave={drag.handleMouseUpOrLeave}
    />
  )
}
